import { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { BarChart3, ExternalLink, Globe, Info, Monitor, Smartphone } from "lucide-react";
import { EmptyState, SegmentedControl } from "../../components/ui";
import {
  GET_PAGE_VIEWS_BY_PAGE,
  GET_PAGE_VIEWS_OVER_TIME,
  GET_TOP_REFERRERS,
  GET_PENDING_VIEW_COUNT,
} from "../../apollo/analytics";
import { toLocalDateStr } from "../../utils/dateRange";
import { usePrimaryColor } from "../../branding/usePrimaryColor";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PageViewByPage {
  path: string;
  views: number;
  desktopViews: number;
  mobileViews: number;
}

interface PageViewOverTime {
  date: string;
  views: number;
}

interface TopReferrer {
  domain: string;
  views: number;
}

// ---------------------------------------------------------------------------
// Date range helpers
// ---------------------------------------------------------------------------

type RangeKey = "7d" | "30d" | "90d" | "custom";

const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
];

function computeDateRange(key: RangeKey): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  if (key === "7d") start.setDate(end.getDate() - 6);
  else if (key === "30d") start.setDate(end.getDate() - 29);
  else if (key === "90d") start.setDate(end.getDate() - 89);
  return { startDate: toLocalDateStr(start), endDate: toLocalDateStr(end) };
}

// ---------------------------------------------------------------------------
// SVG Line Chart (no external deps)
// ---------------------------------------------------------------------------

function ViewsLineChart({
  data,
  color,
}: {
  data: PageViewOverTime[];
  color: string;
}) {
  if (data.length === 0) return null;

  const W = 720;
  const H = 200;
  const PAD_L = 48;
  const PAD_R = 16;
  const PAD_T = 16;
  const PAD_B = 32;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const maxViews = Math.max(...data.map((d) => d.views), 1);
  // Round up max to a nice number for the y axis
  const yMax = Math.ceil(maxViews / (10 ** Math.max(0, Math.floor(Math.log10(maxViews)) - 1))) *
    (10 ** Math.max(0, Math.floor(Math.log10(maxViews)) - 1));

  const points = data.map((d, i) => {
    const x = PAD_L + (data.length === 1 ? plotW / 2 : (i / (data.length - 1)) * plotW);
    const y = PAD_T + plotH - (d.views / yMax) * plotH;
    return { x, y, ...d };
  });

  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = `${pathD} L ${points[points.length - 1].x} ${PAD_T + plotH} L ${points[0].x} ${PAD_T + plotH} Z`;

  // Y-axis labels: 0, mid, max
  const yLabels = [0, Math.round(yMax / 2), yMax];

  // X-axis: show up to ~6 date labels
  const step = Math.max(1, Math.floor(data.length / 6));
  const xLabels = data.filter((_, i) => i % step === 0 || i === data.length - 1);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      style={{ maxHeight: 220 }}
      aria-label="Page views over time chart"
    >
      {/* Grid lines */}
      {yLabels.map((v) => {
        const y = PAD_T + plotH - (v / yMax) * plotH;
        return (
          <g key={v}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={y}
              y2={y}
              className="stroke-ink/6"
              strokeDasharray="4 4"
            />
            <text
              x={PAD_L - 8}
              y={y + 4}
              textAnchor="end"
              className="fill-ink-4"
              style={{ fontSize: 10 }}
            >
              {v.toLocaleString()}
            </text>
          </g>
        );
      })}

      {/* Area fill */}
      <path d={areaD} fill={color} opacity={0.08} />

      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />

      {/* Dots */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill={color}>
          <title>
            {p.date}: {p.views.toLocaleString()} views
          </title>
        </circle>
      ))}

      {/* X-axis labels */}
      {xLabels.map((d) => {
        const idx = data.indexOf(d);
        const x = PAD_L + (data.length === 1 ? plotW / 2 : (idx / (data.length - 1)) * plotW);
        const label = formatShortDate(d.date);
        return (
          <text
            key={d.date}
            x={x}
            y={H - 6}
            textAnchor="middle"
            className="fill-ink-4"
            style={{ fontSize: 10 }}
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function AnalyticsPage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>("30d");
  const primaryColor = usePrimaryColor();

  const { startDate, endDate } = useMemo(() => computeDateRange(rangeKey), [rangeKey]);
  const granularity = rangeKey === "90d" ? "week" : "day";

  const {
    data: byPageData,
    loading: byPageLoading,
  } = useQuery<{ pageViewsByPage: PageViewByPage[] }>(GET_PAGE_VIEWS_BY_PAGE, {
    variables: { startDate, endDate },
  });

  const {
    data: overTimeData,
    loading: overTimeLoading,
  } = useQuery<{ pageViewsOverTime: PageViewOverTime[] }>(GET_PAGE_VIEWS_OVER_TIME, {
    variables: { startDate, endDate, granularity },
  });

  const {
    data: referrersData,
    loading: referrersLoading,
  } = useQuery<{ topReferrers: TopReferrer[] }>(GET_TOP_REFERRERS, {
    variables: { startDate, endDate },
  });

  const { data: pendingData } = useQuery<{ pendingViewCount: number }>(
    GET_PENDING_VIEW_COUNT,
  );

  const byPage = byPageData?.pageViewsByPage ?? [];
  const overTime = overTimeData?.pageViewsOverTime ?? [];
  const referrers = referrersData?.topReferrers ?? [];
  const loading = byPageLoading || overTimeLoading || referrersLoading;
  const totalViews = byPage.reduce((acc, p) => acc + p.views, 0);
  const pendingViews = pendingData?.pendingViewCount ?? 0;
  const isEmpty = !loading && byPage.length === 0 && overTime.length === 0 && referrers.length === 0;

  return (
    <div className="space-y-6">
      {/* Range selector */}
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-ink-3">
          {totalViews > 0 && (
            <span>
              <span className="font-semibold text-ink num">{totalViews.toLocaleString()}</span>{" "}
              total views
            </span>
          )}
        </div>
        <SegmentedControl
          options={RANGE_OPTIONS.map((r) => r.label)}
          value={RANGE_OPTIONS.find((r) => r.key === rangeKey)?.label ?? "30 days"}
          onChange={(label) => {
            const match = RANGE_OPTIONS.find((r) => r.label === label);
            if (match) setRangeKey(match.key);
          }}
        />
      </div>

      {/* Empty state */}
      {isEmpty && (
        <EmptyState
          icon={<BarChart3 size={28} strokeWidth={1.5} />}
          heading="No data yet"
          description="Analytics will appear here once visitors start browsing your site."
        />
      )}

      {/* Pending views hint */}
      {pendingViews > 0 && (isEmpty || totalViews === 0) && (
        <div className="flex items-start gap-3 rounded-lg border border-ink/8 bg-paper-hi px-4 py-3 text-[13px] text-ink-3">
          <Info size={16} strokeWidth={1.8} className="mt-0.5 shrink-0 text-ink-4" />
          <p>
            <span className="font-medium text-ink">{pendingViews.toLocaleString()}</span> page
            {pendingViews === 1 ? " view" : " views"} collected today.{" "}
            {import.meta.env.DEV ? (
              <>
                Run{" "}
                <code className="rounded bg-ink/5 px-1 py-0.5 text-[12px]">
                  python3 jobs/flush_analytics_dev.py
                </code>{" "}
                to flush data into the charts below.
              </>
            ) : (
              "Views from the last few minutes are still processing."
            )}
          </p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !isEmpty && (
        <div className="space-y-6">
          <div className="h-56 animate-pulse rounded-lg bg-paper-hi" />
          <div className="h-40 animate-pulse rounded-lg bg-paper-hi" />
        </div>
      )}

      {/* Views over time chart */}
      {!isEmpty && overTime.length > 0 && (
        <Panel title="Views over time">
          <ViewsLineChart data={overTime} color={primaryColor} />
        </Panel>
      )}

      {/* Views by page table */}
      {!isEmpty && byPage.length > 0 && (
        <Panel title="Page views by page">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-ink-4">
                  <th className="pb-2 pr-4 font-medium">Page</th>
                  <th className="pb-2 px-4 font-medium text-right">Views</th>
                  <th className="pb-2 px-4 font-medium text-right">
                    <span className="inline-flex items-center gap-1">
                      <Monitor size={12} strokeWidth={1.8} /> Desktop
                    </span>
                  </th>
                  <th className="pb-2 pl-4 font-medium text-right">
                    <span className="inline-flex items-center gap-1">
                      <Smartphone size={12} strokeWidth={1.8} /> Mobile
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {byPage.map((row) => (
                  <tr
                    key={row.path}
                    className="border-t border-ink/6 text-ink-2"
                  >
                    <td className="py-2 pr-4 font-mono text-[12px]">
                      {row.path}
                    </td>
                    <td className="py-2 px-4 text-right num font-medium text-ink">
                      {row.views.toLocaleString()}
                    </td>
                    <td className="py-2 px-4 text-right num">
                      {row.desktopViews.toLocaleString()}
                    </td>
                    <td className="py-2 pl-4 text-right num">
                      {row.mobileViews.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* Top referrers table */}
      {!isEmpty && referrers.length > 0 && (
        <Panel title="Top referrers">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-ink-4">
                  <th className="pb-2 pr-4 font-medium">
                    <span className="inline-flex items-center gap-1">
                      <Globe size={12} strokeWidth={1.8} /> Domain
                    </span>
                  </th>
                  <th className="pb-2 pl-4 font-medium text-right">Views</th>
                </tr>
              </thead>
              <tbody>
                {referrers.map((row) => (
                  <tr
                    key={row.domain}
                    className="border-t border-ink/6 text-ink-2"
                  >
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center gap-1.5">
                        {row.domain}
                        <a
                          href={`https://${row.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-ink-5 hover:text-ink-3"
                          aria-label={`Visit ${row.domain}`}
                        >
                          <ExternalLink size={11} strokeWidth={1.8} />
                        </a>
                      </span>
                    </td>
                    <td className="py-2 pl-4 text-right num font-medium text-ink">
                      {row.views.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel wrapper
// ---------------------------------------------------------------------------

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-ink/8 bg-paper p-5">
      <h3 className="mb-4 text-[13px] font-medium text-ink-3">{title}</h3>
      {children}
    </div>
  );
}
